import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import './ResponsiveContent.css';
import Chevron from '../content/Chevron';
import useShelfAutoAdvance from './useShelfAutoAdvance';

// Home opts into edge affordances; other pages keep their existing navigation.
export const ShelfNavigationContext = createContext('default');

// Presentation capacity only. Candidate selection, ranking and paging belong to the data layer.
export const SHELF_PROFILES = {
  albums: { mobile: 2.3, tablet: 4, desktop: 5 },
  compactAlbums: { mobile: 2.3, tablet: 3, desktop: 3 },
  features: { mobile: 1.08, tablet: 2, desktop: 3 },
  reviews: { mobile: 1.08, tablet: 2, desktop: 3 },
  members: { mobile: 1.35, tablet: 3, desktop: 4 },
  people: { mobile: 3, tablet: 4, desktop: 6 },
  hero: { mobile: 1.08, tablet: 2, desktop: 3 },
};

export function shelfWindow(element) {
  const viewport = element.getBoundingClientRect();
  const visible = [...element.children]
    .map((node, index) => ({
      index,
      box: node.getBoundingClientRect(),
    }))
    .filter(({ box }) => box.right > viewport.left + 1 && box.left < viewport.right - 1);
  return {
    previous: element.scrollLeft > 2,
    next: element.scrollWidth - element.clientWidth - element.scrollLeft > 2,
    first: visible.length ? visible[0].index + 1 : 0,
    last: visible.length ? visible[visible.length - 1].index + 1 : 0,
  };
}

export default function ContentShelf({
  label,
  profile = 'albums',
  children,
  className = '',
  navigation = 'arrows',
  autoAdvanceMs = 0,
  loop = false,
}) {
  const { locale } = useWebLocale();
  const edgeNavigation = useContext(ShelfNavigationContext) === 'edges';
  const id = useId();
  const ref = useRef(null);
  const [view, setView] = useState({ previous: false, next: false, first: 0, last: 0 });
  const [pages, setPages] = useState({ count: 1, active: 0, step: 0 });
  const items = React.Children.toArray(children);
  useShelfAutoAdvance(
    ref,
    autoAdvanceMs,
    items.map((item) => item.key).join('|'),
    items.length,
    loop,
  );
  const baseCapacity = SHELF_PROFILES[profile] || SHELF_PROFILES.albums;
  const capacity =
    profile === 'hero'
      ? Object.fromEntries(
          Object.entries(baseCapacity).map(([size, count]) => [
            size,
            Math.min(count, items.length),
          ]),
        )
      : baseCapacity;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => {
      setView(shelfWindow(node));
      if (navigation !== 'dots') return;
      const first = node.children[0];
      const width = first?.getBoundingClientRect().width || node.clientWidth;
      if (width <= 0) return;
      const gap = parseFloat(getComputedStyle(node).columnGap) || 0;
      const perPage = Math.max(1, Math.floor((node.clientWidth + gap + 1) / (width + gap)));
      const columns = new Set(
        [...node.children].map((child) =>
          Math.round(
            child.getBoundingClientRect().left -
              node.getBoundingClientRect().left +
              node.scrollLeft,
          ),
        ),
      ).size;
      const count = node.scrollWidth <= node.clientWidth + 2 ? 1 : Math.ceil(columns / perPage);
      const step = perPage * (width + gap);
      const atEnd = node.scrollWidth - node.clientWidth - node.scrollLeft <= 2;
      setPages({
        count,
        step,
        active: atEnd ? count - 1 : Math.min(count - 1, Math.round(node.scrollLeft / step)),
      });
    };
    update();
    node.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(node);
    window.addEventListener('resize', update);
    return () => {
      node.removeEventListener('scroll', update);
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [items.length, profile, navigation]);
  const duplicate = (item, prefix) =>
    React.cloneElement(item, {
      key: `${prefix}-${item.key}`,
      ...(typeof item.type === 'string'
        ? { 'aria-hidden': true, tabIndex: -1 }
        : { loopDuplicate: true }),
    });
  const move = (direction) => {
    const node = ref.current;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const step = loop
      ? (node?.children[0]?.getBoundingClientRect().width || 0) +
        (parseFloat(getComputedStyle(node).columnGap) || 0)
      : node?.clientWidth;
    node?.scrollBy({ left: direction * step, behavior: reduced ? 'auto' : 'smooth' });
  };
  if (!items.length) return null;
  return (
    <div className={`pp-shelf pp-shelf-${profile} ${className}`}>
      {navigation === 'arrows' && !edgeNavigation && (
        <div className="pp-shelf-controls">
          <span className="pp-shelf-count" aria-live="polite" aria-atomic="true">
            {view.first > 0 ? `${view.first}–${view.last} / ${items.length}` : `${items.length}`}
          </span>
          <button
            type="button"
            aria-label={`${label} · ${locale === 'en' ? 'Previous' : '이전'}`}
            aria-controls={id}
            disabled={!view.previous}
            onClick={() => move(-1)}
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            aria-label={`${label} · ${locale === 'en' ? 'Next' : '다음'}`}
            aria-controls={id}
            disabled={!view.next}
            onClick={() => move(1)}
          >
            <Chevron />
          </button>
        </div>
      )}
      <div className="pp-shelf-viewport" data-previous={view.previous} data-next={view.next}>
        <div
          id={id}
          ref={ref}
          className="pp-shelf-track"
          role="region"
          aria-label={label}
          tabIndex={0}
          style={{
            '--shelf-mobile': capacity.mobile,
            '--shelf-tablet': capacity.tablet,
            '--shelf-desktop': capacity.desktop,
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              move(event.key === 'ArrowRight' ? 1 : -1);
            }
          }}
        >
          {loop && items.length > 1 && items.map((item) => duplicate(item, 'loop-before'))}
          {items}
          {loop && items.length > 1 && items.map((item) => duplicate(item, 'loop-after'))}
        </div>
        {edgeNavigation && (
          <>
            <button
              className="pp-shelf-edge pp-shelf-edge-left"
              type="button"
              aria-label={`${label} · ${locale === 'en' ? 'Previous' : '이전'}`}
              aria-controls={id}
              disabled={!view.previous}
              onClick={() => move(-1)}
            >
              <Chevron direction="left" />
            </button>
            <button
              className="pp-shelf-edge pp-shelf-edge-right"
              type="button"
              aria-label={`${label} · ${locale === 'en' ? 'Next' : '다음'}`}
              aria-controls={id}
              disabled={!view.next}
              onClick={() => move(1)}
            >
              <Chevron />
            </button>
          </>
        )}
      </div>
      {navigation === 'dots' && pages.count > 1 && (
        <div
          className="pp-shelf-dots"
          aria-label={`${label} · ${locale === 'en' ? 'Pages' : '페이지'}`}
        >
          {Array.from({ length: pages.count }, (_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`${label} · ${index + 1}${locale === 'en' ? ' page' : '페이지'}`}
              aria-controls={id}
              aria-current={pages.active === index ? 'true' : undefined}
              onClick={() =>
                ref.current?.scrollTo({
                  left: Math.min(
                    index * pages.step,
                    ref.current.scrollWidth - ref.current.clientWidth,
                  ),
                  behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
